import React, { useState, useRef, useEffect } from "react";
import { apiPost, apiGet, apiPut, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { useForm } from "react-hook-form";
import useToastContext from "hooks/useToastContext";
import OInputField from "components/reusable/OInputField";
import { useTranslation } from "react-i18next";
import { preventMaxInput } from "utils/validations";
import { AiFillEdit, AiFillDelete, AiOutlineCloudSync } from "react-icons/ai";
import { FaSync, FaCheckCircle, FaExclamationCircle, FaSearch, FaExclamationTriangle } from "react-icons/fa";
import helpers from "utils/helpers";
import { US_STATES, getStateCode, getStateName } from "utils/usStates";
import AddressAutocomplete from "components/GoogleMap/AddressAutocomplete";
import LocationMap from "components/GoogleMap/LocationMap";
import { geocodeAddress } from "utils/googleMaps";

const LocationManage = ({ show, handleClose, locations, refreshLocations }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  
  // ─── NEW: Payroll Service Status State ───
  const [isServiceActive, setIsServiceActive] = useState(false);
  const [checkingService, setCheckingService] = useState(true);

  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState({});
  const [coordinates, setCoordinates] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true });

  const watchedValues = watch();
  const geocodeTimeoutRef = useRef(null);
  const skipAutoGeocodeRef = useRef(false);

  // ─── NEW: Check Payroll Status on Modal Open ───
  useEffect(() => {
    if (!show) return;
    
    const checkStatus = async () => {
      try {
        setCheckingService(true);
        const res = await apiGet(apiPath.payrollStatus);
        if (res?.data?.success && res.data.results?.isActive) {
          setIsServiceActive(true);
        } else {
          setIsServiceActive(false);
        }
      } catch (error) {
        console.error("Failed to check payroll status", error);
        setIsServiceActive(false);
      } finally {
        setCheckingService(false);
      }
    };
    
    checkStatus();
  }, [show]);

  const isAddressComplete = () => {
    const values = watch();
    return values.line1 && values.city && values.state && values.postalCode;
  };

  const handleAddNew = () => {
    setIsAdding(true);
    setIsEditing(false);
    setCurrentLocation(null);
    setCoordinates(null);
    reset({
      name: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      timezone: "",
      workingHoursStart: "09:00",
      workingHoursEnd: "18:00",
    });
  };

  const handleEditLocation = (location) => {
    setIsEditing(true);
    setIsAdding(true);
    setCurrentLocation(location);
    setValue("name", location.name);
    setValue("line1", location.address?.line1 || "");
    setValue("line2", location.address?.line2 || "");
    setValue("city", location.address?.city || "");
    setValue("state", location.address?.state || "");
    setValue("postalCode", location.address?.postalCode || "");
    setValue("timezone", location.timezone);
    setValue("workingHoursStart", location.workingHours?.start || "09:00");
    setValue("workingHoursEnd", location.workingHours?.end || "18:00");

    if (location.evereeLatitude && location.evereeLongitude) {
      skipAutoGeocodeRef.current = true;
      setCoordinates({
        latitude: location.evereeLatitude,
        longitude: location.evereeLongitude,
      });
    }
  };

  const handlePlaceSelect = (place) => {
    if (place) {
      setValue("name", place.name || place.formattedAddress || "");
      
      if (place.addressComponents) {
        const { streetNumber, streetName, city, state, postalCode } = place.addressComponents;
        const line1 = streetNumber && streetName 
          ? `${streetNumber} ${streetName}` 
          : place.formattedAddress;

        setValue("line1", line1 || "");
        setValue("city", city || "");
        setValue("state", state || "");
        setValue("postalCode", postalCode || "");
      }

      if (place.latitude && place.longitude) {
        skipAutoGeocodeRef.current = true;
        setCoordinates({
          latitude: place.latitude,
          longitude: place.longitude,
        });
      }
    }
  };

  const handleMapLocationSelect = (addressData) => {
    if (addressData) {
      skipAutoGeocodeRef.current = true;
      setValue("line1", addressData.line1 || "");
      setValue("city", addressData.city || "");
      setValue("state", addressData.state || "");
      setValue("postalCode", addressData.postalCode || "");
      setCoordinates({
        latitude: addressData.latitude,
        longitude: addressData.longitude,
      });
    }
  };

  const handleGeocodeCurrentAddress = async () => {
    const formData = watch();
    const fullAddress = `${formData.line1}, ${formData.city}, ${formData.state} ${formData.postalCode}`;

    if (fullAddress.length < 10) {
      notification.error("Please enter a complete address");
      return;
    }

    setIsGeocoding(true);
    try {
      const result = await geocodeAddress(fullAddress);
      if (result) {
        setCoordinates({
          latitude: result.latitude,
          longitude: result.longitude,
        });
        notification.success("Address geocoded successfully!");
      }
    } catch (error) {
      notification.error("Failed to geocode address. Please check the address.");
    } finally {
      setIsGeocoding(false);
    }
  };

  useEffect(() => {
    if (!isAdding) return;
    if (skipAutoGeocodeRef.current) {
      skipAutoGeocodeRef.current = false;
      return;
    }
    if (!isAddressComplete()) return;

    if (geocodeTimeoutRef.current) clearTimeout(geocodeTimeoutRef.current);

    geocodeTimeoutRef.current = setTimeout(() => {
      handleGeocodeCurrentAddress();
    }, 800);

    return () => clearTimeout(geocodeTimeoutRef.current);
  }, [watchedValues.line1, watchedValues.city, watchedValues.state, watchedValues.postalCode, isAdding]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      const payload = {
        name: data.name,
        address: {
          line1: data.line1,
          line2: data.line2 || "",
          city: data.city,
          state: data.state,
          postalCode: data.postalCode,
        },
        timezone: data.timezone,
        workingHours: {
          start: data.workingHoursStart,
          end: data.workingHoursEnd,
        },
        breakRules: [
          {
            name: "Lunch Break",
            startTime: "13:00",
            endTime: "14:00",
            isPaid: false,
            duration: 60,
          },
        ],
        overtimeRules: {
          dailyThreshold: 8,
          weeklyThreshold: 40,
          multiplier: 1.5,
        },
        latitude: coordinates?.latitude || null,
        longitude: coordinates?.longitude || null,
      };

      let res;
      if (isEditing && currentLocation?._id) {
        res = await apiPut(`${apiPath.updateLocation}/${currentLocation._id}`, payload);
      } else {
        res = await apiPost(apiPath.createLocation, payload);
      }

      if (res?.data?.success) {
        refreshLocations();
        notification.success(res?.data?.message);
        setIsAdding(false);
        setIsEditing(false);
        setCurrentLocation(null);
        setCoordinates(null);
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.log("err:", err);
      notification.error(t("SOMETHING_WENT_WRONG"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    helpers.alertFunction(
      "Are you sure want to delete this location?",
      locationId,
      async (id) => {
        try {
          const res = await apiDelete(`${apiPath.deleteLocation}/${id}`);
          if (res?.data?.success) {
            refreshLocations();
            notification.success(res?.data?.message);
          } else {
            notification.error(res?.data?.message);
          }
        } catch (err) {
          notification.error(t("SOMETHING_WENT_WRONG"));
        }
      },
      true
    );
  };

  const handleSyncLocation = async (locationId) => {
    if (!isServiceActive) return;

    setSyncInProgress((prev) => ({ ...prev, [locationId]: true }));
    try {
      const res = await apiPost(`${apiPath.syncLocation}/${locationId}`, {});
      if (res?.data?.success) {
        notification.success("Location synced with Everee successfully");
        refreshLocations();
      } else {
        notification.error(res?.data?.message || "Sync failed");
      }
    } catch (err) {
      notification.error(t("SOMETHING_WENT_WRONG"));
    } finally {
      setSyncInProgress((prev) => ({ ...prev, [locationId]: false }));
    }
  };

  const handleBulkSync = async () => {
    if (!isServiceActive) return;
    setIsSyncing(true);
    try {
      const res = await apiPost(apiPath.syncAllLocations, {});
      if (res?.data?.success) {
        const results = res.data.data;
        const successCount = results.filter((r) => r.success).length;
        const failCount = results.filter((r) => !r.success).length;
        notification.success(
          `Synced ${successCount} locations to Everee${failCount > 0 ? `, ${failCount} failed` : ""}`
        );
        refreshLocations();
      } else {
        notification.error(res?.data?.message || "Bulk sync failed");
      }
    } catch (err) {
      notification.error(t("SOMETHING_WENT_WRONG"));
    } finally {
      setIsSyncing(false);
    }
  };

  const getSyncStatusBadge = (status) => {
    switch (status) {
      case "synced":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            <FaCheckCircle className="mr-1 w-3 h-3" />
            Synced
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            <FaExclamationCircle className="mr-1 w-3 h-3" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
            <FaSync className="mr-1 w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  const formatAddress = (address) => {
    if (!address) return "";
    const stateName = getStateName(address.state);
    return `${address.line1}, ${address.city}, ${stateName} ${address.postalCode}${
      address.line2 ? `, ${address.line2}` : ""
    }`;
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black opacity-40" onClick={handleClose} />

      <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden outline-none pointer-events-none">
        <div className="relative w-full my-6 mx-auto max-w-6xl pointer-events-auto">
          <div className="border-0 rounded-xl shadow-xl relative flex flex-col w-full bg-white outline-none max-h-[90vh]">

            <div className="p-4 flex items-center justify-between border-b border-solid border-slate-100 rounded-t">
              <h3 className="font-bold text-lg text-gray-800">
                {t("MANAGE_LOCATIONS")}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="h-8 w-8 ml-auto flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <span className="text-2xl font-light">×</span>
                </button>
              </div>
            </div>

            <div className="p-5 overflow-y-auto max-h-[75vh]">
              
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">
                    {t("EXISTING_LOCATIONS")} ({locations?.length || 0})
                  </h4>
                  <button
                    type="button"
                    onClick={handleAddNew}
                    className="text-sm bg-[#4F46E5] text-white px-3 py-1.5 rounded-md hover:bg-[#4338CA] transition-colors"
                  >
                    + {t("ADD_LOCATION")}
                  </button>
                </div>

                {locations?.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-lg">
                    {t("NO_LOCATIONS_FOUND")}
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-xs text-left text-[#A5A5A5]">
                      <thead className="text-gray-900 text-xs border-b bg-[#E1E6EE]">
                        <tr>
                          <th className="px-4 py-2">{t("NAME")}</th>
                          <th className="px-4 py-2">{t("ADDRESS")}</th>
                          <th className="px-4 py-2">{t("TIMEZONE")}</th>
                          <th className="px-4 py-2">{t("WORKING_HOURS")}</th>
                          {isServiceActive && (
                            <th className="px-4 py-2 text-center">Everee Sync</th>
                          )}
                          <th className="px-4 py-2 text-center">{t("O_ACTION")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locations?.map((loc) => (
                          <tr key={loc._id} className="bg-white border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-900 font-medium">
                              {loc.name}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {formatAddress(loc.address)}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {(loc.timezone || loc.evereeTimeZone || "N/A").replace(/_/g, ' ')}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {loc.workingHours?.start} - {loc.workingHours?.end}
                            </td>
                            
                            {/* CONDITIONALLY RENDER EVEREE SYNC COLUMN */}
                            {isServiceActive && (
                              <td className="px-4 py-3 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  {getSyncStatusBadge(loc.evereeSyncStatus || "pending")}
                                  {loc.evereeId && (
                                    <span className="text-[10px] text-gray-400">
                                      ID: {loc.evereeId}
                                    </span>
                                  )}
                                  {loc.evereeLatitude && (
                                    <span className="text-[9px] text-gray-400">
                                      ({loc.evereeLatitude.toFixed(4)}, {loc.evereeLongitude?.toFixed(4)})
                                    </span>
                                  )}
                                </div>
                              </td>
                            )}
                            
                            <td className="px-4 py-3 text-center">
                              <div className="flex justify-center gap-2">
                                {/* Only show Sync if service is active */}
                                {isServiceActive && (
                                  <button
                                    type="button"
                                    onClick={() => handleSyncLocation(loc._id)}
                                    disabled={syncInProgress[loc._id]}
                                    className="p-1.5 hover:bg-green-50 rounded text-green-600 transition-colors disabled:opacity-50"
                                    title="Sync with Everee"
                                  >
                                    <FaSync className={`w-3 h-3 ${syncInProgress[loc._id] ? "animate-spin" : ""}`} />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteLocation(loc._id)}
                                  className="p-1.5 hover:bg-red-50 rounded text-red-600 transition-colors"
                                  title="Delete"
                                >
                                  <AiFillDelete className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {isAdding && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">
                    {isEditing ? t("EDIT_LOCATION") : t("ADD_NEW_LOCATION")}
                  </h4>
                  
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-4">
                      <div className="relative">
                        <AddressAutocomplete
                          value={watch("name") || ""}
                          onChange={(value) => setValue("name", value)}
                          onSelect={handlePlaceSelect}
                          placeholder="Search for a location or address..."
                          className="w-full px-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent text-base"
                        />
                      </div>
                      {errors.name && (
                        <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    <div className="flex gap-6">
                      <div className="flex-1 min-w-0">
                        <div className="grid grid-cols-1 gap-y-4">
                          <OInputField
                            inputLabel={<>{t("LOCATION_NAME")}<span className="text-red-500">*</span></>}
                            wrapperClassName="relative z-0 w-full group"
                            type="text"
                            name="name"
                            onInput={(e) => preventMaxInput(e, 100)}
                            register={register("name", { required: t("REQUIRED_FIELD") })}
                            errors={errors}
                          />

                          <OInputField
                            inputLabel={<>{t("STREET_ADDRESS_LINE1")}<span className="text-red-500">*</span></>}
                            wrapperClassName="relative z-0 w-full group"
                            type="text"
                            name="line1"
                            onInput={(e) => preventMaxInput(e, 200)}
                            register={register("line1", { required: t("REQUIRED_FIELD") })}
                            errors={errors}
                          />

                          <OInputField
                            inputLabel={<>{t("STREET_ADDRESS_LINE2")}</>}
                            wrapperClassName="relative z-0 w-full group"
                            type="text"
                            name="line2"
                            onInput={(e) => preventMaxInput(e, 200)}
                            register={register("line2")}
                            errors={errors}
                          />

                          <div className="grid grid-cols-2 gap-4">
                            <OInputField
                              inputLabel={<>{t("CITY")}<span className="text-red-500">*</span></>}
                              wrapperClassName="relative z-0 w-full group"
                              type="text"
                              name="city"
                              onInput={(e) => preventMaxInput(e, 100)}
                              register={register("city", { required: t("REQUIRED_FIELD") })}
                              errors={errors}
                            />

                            <div>
                              <select
                                {...register("state", { required: t("REQUIRED_FIELD") })}
                                className="w-full h-[55px] px-3 py-2 border-2 border-[#DFDFDF] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent"
                              >
                                <option value="">{t("SELECT_STATE")}</option>
                                {US_STATES.map((state) => (
                                  <option key={state.code} value={state.code}>
                                    {state.name}
                                  </option>
                                ))}
                              </select>
                              {errors.state && (
                                <p className="text-red-500 text-xs mt-1">{errors.state.message}</p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <OInputField
                              inputLabel={<>{t("POSTAL_CODE")}<span className="text-red-500">*</span></>}
                              wrapperClassName="relative z-0 w-full group"
                              type="text"
                              name="postalCode"
                              onInput={(e) => preventMaxInput(e, 10)}
                              register={register("postalCode", { required: t("REQUIRED_FIELD") })}
                              errors={errors}
                            />

                            <OInputField
                              inputLabel={<>{t("TIMEZONE")}</>}
                              wrapperClassName="relative z-0 w-full group"
                              type="text"
                              name="timezone"
                              onInput={(e) => preventMaxInput(e, 50)}
                              register={register("timezone")}
                              errors={errors}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <OInputField
                              inputLabel={<>{t("WORK_START_TIME")}<span className="text-red-500">*</span></>}
                              wrapperClassName="relative z-0 w-full group"
                              type="time"
                              name="workingHoursStart"
                              register={register("workingHoursStart", {
                                required: t("REQUIRED_FIELD"),
                              })}
                              errors={errors}
                            />
                            <OInputField
                              inputLabel={<>{t("WORK_END_TIME")}<span className="text-red-500">*</span></>}
                              wrapperClassName="relative z-0 w-full group"
                              type="time"
                              name="workingHoursEnd"
                              register={register("workingHoursEnd", {
                                required: t("REQUIRED_FIELD"),
                              })}
                              errors={errors}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="w-[400px] flex-shrink-0">
                        <div className="sticky top-0">
                          <LocationMap
                            latitude={coordinates?.latitude}
                            longitude={coordinates?.longitude}
                            address={`${watch("line1")}, ${watch("city")}, ${watch("state")} ${watch("postalCode")}`}
                            onLocationChange={handleMapLocationSelect}
                            height="400px"
                            zoom={15}
                          />
                          
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={handleGeocodeCurrentAddress}
                              disabled={isGeocoding || !isAddressComplete()}
                              className="w-full bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {isGeocoding ? (
                                <span className="flex items-center justify-center">
                                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
                                  Verifying...
                                </span>
                              ) : (
                                "Verify Address on Map"
                              )}
                            </button>

                            {coordinates && (
                              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                                <div className="text-xs text-green-700 font-medium">
                                  ✓ Address Verified
                                </div>
                                <div className="text-xs text-green-600 mt-1">
                                  Lat: {coordinates.latitude.toFixed(6)}, Lng: {coordinates.longitude.toFixed(6)}
                                </div>
                              </div>
                            )}
                          </div>

                          {isGeocoding && (
                            <p className="text-xs text-gray-500 mt-2">Locating address on map...</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAdding(false);
                          setIsEditing(false);
                          setCoordinates(null);
                        }}
                        className="text-gray-700 bg-gray-200 font-medium px-6 py-2 text-sm rounded-md hover:bg-gray-300"
                      >
                        {t("O_CANCEL")}
                      </button>
                      {helpers.ternaryCondition(
                        isLoading,
                        <div className="flex justify-center bg-[#4F46E5] px-6 py-2 rounded-md">
                          <div className="loading-spinner h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        </div>,
                        <button
                          type="submit"
                          className="bg-[#4F46E5] text-white font-medium px-6 py-2 text-sm rounded-md hover:bg-[#4338CA]"
                        >
                          {isEditing ? t("O_UPDATE") : t("O_ADD")}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationManage;